import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import type { HelperTextProps } from './HelperText';
import { HelperTextContent } from './HelperText';
import useStyles from './TextField.styles';
export interface TextFieldProps extends Omit<MuiTextFieldProps, 'variant'> {
  variant?: MuiTextFieldProps['variant'] | 'simple' | 'fill';
  readOnly?: boolean;
  valid?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  extraLabel?: ReactNode;
}
export const TextField = forwardRef<any, TextFieldProps>(function TextField({
  variant = 'simple',
  required = false,
  InputProps,
  InputLabelProps,
  readOnly,
  startIcon,
  endIcon,
  helperText,
  valid,
  label,
  extraLabel,
  ...restProps
}, ref): JSX.Element {
  const {
    classes,
    cx
  } = useStyles();
  const simple = variant === 'simple';
  const fill = variant === 'fill';
  const MyInputProps = useMemo<TextFieldProps['InputProps']>(() => ({
    notched: simple || fill ? false : undefined,
    readOnly,
    startAdornment: startIcon ? <InputAdornment position='start'>{startIcon}</InputAdornment> : null,
    endAdornment: endIcon ? <InputAdornment position='end'>{endIcon}</InputAdornment> : null,
    ...InputProps,
    classes: {
      ...InputProps?.classes,
      root: cx(InputProps?.classes?.root, {
        [classes.fill]: fill,
        [classes.readOnly]: readOnly,
        [classes.fillReadOnly]: fill && readOnly
      })
    }
  }), [simple, fill, InputProps, readOnly, startIcon, endIcon, classes, cx]);
  const MyInputLabelProps = useMemo<TextFieldProps['InputLabelProps']>(() => ({
    shrink: simple || fill ? true : undefined,
    ...InputLabelProps,
    classes: {
      root: cx({
        [classes.simpleLabel]: simple || fill
      })
    },
    required
  }), [simple, fill, InputLabelProps, classes, cx, required]);
  let helperTextType: HelperTextProps['type'] = 'info';
  if (valid) {
    helperTextType = 'success';
  }
  if (restProps.error) {
    helperTextType = 'error';
  }
  const customLabel = extraLabel ? <div className={classes.labelContainer}>
      <span>{label}</span>
      {extraLabel}
    </div> : label;
  return <MuiTextField helperText={Boolean(helperText) && <HelperTextContent type={helperTextType}>
            {helperText}
          </HelperTextContent>} InputLabelProps={{
    ...MyInputLabelProps,
    required
  }} InputProps={MyInputProps} inputRef={ref} label={customLabel} variant={simple || fill ? 'outlined' : variant} {...restProps} />;
});